'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface ReviewSectionProps {
  isLoading?: boolean;
}

const reviewDimensions = [
  {
    title: 'Architecture',
    description:
      'Coupling and cohesion, dead and unreachable code, near-duplicate functions, scattered configuration, oversized public APIs.',
  },
  {
    title: 'Reliability',
    description:
      'Unhandled and swallowed errors, errors overwritten before they are checked, null dereference after an error, resource leaks, missing timeouts.',
  },
  {
    title: 'Testing',
    description:
      'Tests with no assertions, assertions that pass on any value, flaky tests, mock abuse, test pollution, missing tests.',
  },
  {
    title: 'Performance',
    description:
      'N+1 I/O in loops, O(n²) string building, unbounded collections, cache growth with no eviction, nested scans.',
  },
  {
    title: 'Security',
    description:
      'Hardcoded secrets and credentials, SQL and command injection, untrusted input that reaches dangerous calls across files.',
  },
  {
    title: 'Concurrency',
    description:
      'Detached and unmonitored tasks, unawaited async calls, stale closure capture, blocking calls in async paths.',
  },
  {
    title: 'Structure',
    description:
      'Cyclomatic complexity, deep nesting, long functions, long parameter lists, oversized files, dead branches.',
  },
  {
    title: 'Hygiene',
    description:
      'Dead functions, unused exports, duplicate definitions, magic numbers and strings, commented-out code, ignored warnings with no reason given.',
  },
  {
    title: 'Toolchain gates',
    description:
      'Your linters and type checks run on every review and merge into one report: go vet, golangci-lint, ruff, eslint, tsc, cargo clippy.',
  },
];

export function ReviewSection({ isLoading = false }: ReviewSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-8 h-10 w-[50%]" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
              <Skeleton key={i} className="h-40 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-4 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          Automated senior-level review.
        </h2>
        <p
          className="text-foreground/70 mb-10 text-center text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          Every review finding points to the exact line and explains why:
        </p>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviewDimensions.map((dimension) => (
            <div
              key={dimension.title}
              className="rounded-xl p-6 transition-all duration-200 hover:scale-[1.02]"
              style={{
                background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
                border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
              }}
            >
              <h3
                className="mb-3 text-lg font-semibold md:text-xl"
                style={{ fontFamily: theme.fonts.display }}
              >
                {dimension.title}
              </h3>
              <p
                className="text-foreground/70 text-sm md:text-base"
                style={{ fontFamily: theme.fonts.body }}
              >
                {dimension.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
